import {Injectable} from '@angular/core';
import {UserRole} from '../authentication/user-role';
import {AuthenticationService} from '../authentication/authentication.service';

export class Dashboard {
  title: string;
  matIcon: string;
  /**
   * For internal dashboards you should also specify favicon image name. It will be updated along with tab name. Don't
   * forget to add favicon png image to assets/images/favicon.
   */
  faviconImageName?: string;
  allowedRoles?: string[];
  routerLink?: string;
  externalUrl?: string;
  geckoAccessRequestEnabled?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardsService {

  static readonly LOCALIZATOR_DASHBOARD: Dashboard = {
    title: 'Localizator (Old)',
    routerLink: 'localizator',
    matIcon: 'public',
    faviconImageName: 'public',
    allowedRoles: UserRole.LOCALIZATOR_ALL_ROLES
  };
  static readonly LOCALIZATOR_NEW_DASHBOARD: Dashboard = {
    title: 'Localizator (New)',
    externalUrl: 'https://localizator.visual7.app/projects',
    matIcon: 'public',
    allowedRoles: [UserRole.LOCALIZATOR_V2_VIEWER]
  };
  static readonly APP_USER_STATE_TEMPLATES_DASHBOARD: Dashboard = {
    title: 'App User State Templates',
    routerLink: 'app-user-state-templates',
    matIcon: 'people',
    faviconImageName: 'people',
    allowedRoles: [UserRole.O7DEVICES_EDITOR],
    geckoAccessRequestEnabled: true
  };
  static readonly STARLITE_SYMBOLICATOR_DASHBOARD: Dashboard = {
    title: 'Starlite Symbolicator',
    routerLink: 'starlite-symbolicator',
    matIcon: 'translate',
    faviconImageName: 'translate',
    allowedRoles: [UserRole.STARLITE_SYMBOLICATOR]
  };
  static readonly HOME_DASHBOARD: Dashboard = {
    title: 'Home',
    routerLink: '/',
    matIcon: 'home',
    // For some reason home.png doesn't work
    faviconImageName: 'visual7'
  };
  static readonly REQUEST_BUILDER_DASHBOARD: Dashboard = {
    title: 'Request Builder',
    routerLink: 'request-builder',
    matIcon: 'border_all',
    faviconImageName: 'border_all',
    allowedRoles: [UserRole.O7DEVICES_EDITOR],
    geckoAccessRequestEnabled: true
  };
  static readonly TOOLS_DASHBOARD: Dashboard = {
    title: 'Tools',
    routerLink: 'tools',
    matIcon: 'build',
    faviconImageName: 'build',
  };
  static readonly CROSSPROMO_KPIS_DASHBOARD: Dashboard = {
    title: 'Crosspromo',
    routerLink: 'crosspromo',
    matIcon: 'trending_up',
    faviconImageName: 'trending',
    allowedRoles: [UserRole.NEWS]
  };
  static readonly EVENTS_HORIZON_DASHBOARD: Dashboard = {
    title: 'Events Horizon',
    routerLink: 'events-horizon',
    matIcon: 'event',
    faviconImageName: 'event',
    allowedRoles: [UserRole.EVENTS_HORIZON_EDITOR],
    geckoAccessRequestEnabled: true
  };
  static readonly MEDIATION_DASHBOARD: Dashboard = {
    title: 'Mediation',
    routerLink: 'mediation',
    matIcon: 'dashboard',
    faviconImageName: 'dashboard',
    allowedRoles: [UserRole.MEDIATION_VIEWER],
    geckoAccessRequestEnabled: true
  };

  constructor(private authenticationService: AuthenticationService) {
  }

  getDashboards(): Dashboard[] {
    const authService: AuthenticationService = this.authenticationService;
    return dashboards.filter((dashboard) => authService.hasAnyRole(dashboard.allowedRoles));
  }

  getDashboardByTitle(title: string): Dashboard | undefined {
    return dashboards.find((dashboard: Dashboard) => dashboard.title === title);
  }
}

const dashboards: Dashboard[] = [
  DashboardsService.HOME_DASHBOARD,
  {
    title: 'O7Devices',
    externalUrl: 'https://o7devices.visual7.app/',
    matIcon: 'devices',
    allowedRoles: [
      UserRole.O7DEVICES_EDITOR
    ]
  },
  DashboardsService.REQUEST_BUILDER_DASHBOARD,
  {
    title: 'Gdoc Updater',
    externalUrl: 'https://gdoc.visual7.app/',
    matIcon: 'insert_drive_file',
    allowedRoles: [UserRole.GDOC_VIEWER]
  },
  {
    title: 'Config7',
    externalUrl: 'https://config7.visual7.app',
    matIcon: 'account_tree',
    allowedRoles: [UserRole.CONFIG7_VIEWER]
  },
  {
    title: 'AB tests',
    externalUrl: 'https://ab-tests.visual7.app/',
    matIcon: 'tune',
    allowedRoles: [UserRole.AB_TESTS_VIEWER]
  },
  DashboardsService.LOCALIZATOR_DASHBOARD,
  DashboardsService.LOCALIZATOR_NEW_DASHBOARD,
  DashboardsService.APP_USER_STATE_TEMPLATES_DASHBOARD,
  DashboardsService.EVENTS_HORIZON_DASHBOARD,
  DashboardsService.TOOLS_DASHBOARD,
  DashboardsService.STARLITE_SYMBOLICATOR_DASHBOARD,
  {
    title: 'Gladiators rankings',
    externalUrl: 'https://mythic-legends-rankings.visual7.app/',
    matIcon: 'leaderboard',
    allowedRoles: [UserRole.GLADIATORS_RANKINGS]
  },
  DashboardsService.CROSSPROMO_KPIS_DASHBOARD,
  {
    title: 'Crosspromo DSP',
    externalUrl: 'https://dsp.visual7.app/',
    matIcon: 'copyright',
    allowedRoles: [UserRole.CROSSPROMO_DSP_EDITOR]
  },
  {
    title: 'Coupons',
    externalUrl: 'https://coupons.visual7.app/',
    matIcon: 'card_giftcard',
    allowedRoles: [UserRole.COUPONS_EDITOR]
  },
  {
    title: 'Placements',
    externalUrl: 'https://placements.visual7.app/',
    matIcon: 'device_hub',
    allowedRoles: [UserRole.PLACEMENTS_EDITOR]
  },
  DashboardsService.MEDIATION_DASHBOARD,
  {
    title: 'News',
    externalUrl: 'https://visual7-legacy.outfit7.net/dashboard/news/',
    matIcon: 'list_alt',
    allowedRoles: [UserRole.NEWS]
  },
  {
    title: 'Campaigns',
    externalUrl: 'https://visual7-legacy.outfit7.net/dashboard/promo/',
    matIcon: 'live_tv',
    allowedRoles: [UserRole.AD_CAMPAIGNS]
  },
  {
    title: 'User Support',
    externalUrl: 'https://visual7-legacy.outfit7.net/dashboard/user-support/',
    matIcon: 'headset_mic',
    allowedRoles: [UserRole.USER_SUPPORT]
  },
  {
    title: 'Punisher',
    externalUrl: 'https://visual7-legacy.outfit7.net/dashboard/fake-apps/',
    matIcon: 'copyright',
    allowedRoles: [UserRole.PUNISHER]
  },
  {
    title: 'Manage account',
    externalUrl: 'https://accounts-be.outfit7.net/auth/realms/backend/account',
    matIcon: 'person',
  },
  {
    title: 'Build sizes',
    externalUrl: 'https://datastudio.google.com/s/tWwBFA6dWZk',
    matIcon: 'square_foot'
  },
  {
    title: 'User support V2',
    externalUrl: 'https://user-support.visual7.app/',
    matIcon: 'help'
  },
  {
    title: 'Device Data Overview',
    externalUrl: 'https://lookerstudio.google.com/u/0/reporting/d3c50600-00e3-442d-b71d-3cba555c9a01/page/p_8kuwvwj6dd',
    matIcon: 'phone_android',
  },
  {
    title: 'AppRocket',
    externalUrl: 'https://app-rocket.visual7.app/',
    matIcon: 'rocket_launch',
    allowedRoles: [UserRole.APPROCKET_BUILD_VIEWER]
  },
  {
    title: 'Play7',
    externalUrl: 'https://play7-dashboard-web-890830733959.us-central1.run.app/',
    matIcon: 'sports_esports',
  }
];
